import { Button, PasswordInput, Stack, Text } from "@mantine/core";
import { InlineError } from "./ui";
import { useState } from "react";
import { ApiClientError, api } from "../lib/api";

/**
 * Changing your own password. One form, two places:
 *
 *  - the forced screen, when a bootstrap password still has to be replaced
 *  - the profile page, any time afterwards
 *
 * Shared rather than duplicated because the rules it has to express - the
 * 12-character minimum, the confirm-match, and the server's list of specific
 * complaints - would otherwise drift apart, and the version people use less
 * often is the one that would go stale.
 *
 * The current password is always required, including in the profile. A live
 * session is not proof of identity at the moment of the change: it could be a
 * borrowed phone or an unlocked desk, and re-typing the old password is what
 * makes a stolen session unable to lock the owner out of their own account.
 */
export function PasswordChangeForm({
  submitLabel,
  onDone,
}: {
  submitLabel: string;
  /** Called after a successful change. */
  onDone: () => void | Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [problems, setProblems] = useState<string[]>();

  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const sameAsOld = newPassword.length > 0 && newPassword === currentPassword;
  const canSubmit =
    !busy &&
    currentPassword.length > 0 &&
    newPassword.length >= 12 &&
    confirm === newPassword &&
    !sameAsOld;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);
    setProblems(undefined);
    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      // Cleared before handing back, so a re-render of this form - or a
      // navigation that keeps it mounted - never leaves a password in state.
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
      await onDone();
    } catch (caught) {
      if (caught instanceof ApiClientError) {
        setError(caught.message);
        if (Array.isArray(caught.details)) {
          setProblems(caught.details as string[]);
        }
      } else {
        setError("Could not change your password.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="sm">
        {/* PasswordInput brings its own show/hide toggle, so the separate
            "Show" button is gone - one control, and it is the one people
            already know from every other site. */}
        <PasswordInput
          id="current"
          label="Current password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.currentTarget.value)}
          required
        />

        <PasswordInput
          id="new"
          label="New password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.currentTarget.value)}
          required
          minLength={12}
          description="At least 12 characters. A few unrelated words are stronger and easier to remember than a short password with symbols in it."
          error={
            sameAsOld
              ? "That is your current password. Choose a different one."
              : undefined
          }
        />

        <PasswordInput
          id="confirm"
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.currentTarget.value)}
          required
          error={mismatch ? "Those two do not match." : undefined}
        />

        {error && <InlineError>{error}</InlineError>}
        {problems && problems.length > 0 && (
          <Stack gap={4}>
            {problems.map((problem) => (
              <Text key={problem} fz="sm" c="yellow.8">
                {problem}
              </Text>
            ))}
          </Stack>
        )}

        <Button type="submit" size="md" fullWidth loading={busy} disabled={!canSubmit}>
          {submitLabel}
        </Button>
      </Stack>
    </form>
  );
}
